import React, { useEffect } from 'react';
import SeoArticle from '../components/SeoArticle';
import { ABOUT_ARTICLE } from '../seo/seoContent';
import Navbar from '../components/Navbar';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import { heroTemplates, intlTemplates } from '../components/Hero';
import { useOwnezzyContact, whatsappLink } from '../legal/contact';
import './AboutPage.css';

// /about on ownezzy.com: who we are and what Ownezzy does, written for shop owners and search engines.
// The page sets its own title, description, canonical link and AboutPage structured data (index.html holds the
// home page's).
const TITLE = 'About Ownezzy | Online Store Websites for Shops in India and Worldwide';
const DESCRIPTION = 'Ownezzy helps small shops, home businesses and Instagram, Facebook and WhatsApp sellers in India and around the world (USA, Canada, UK, Europe, UAE, Saudi Arabia, Japan, Australia) get a free ready-made website with an admin panel, Cash on Delivery orders and their own web address.';

const STATS = [
  ['bi-lightning-charge', '2 minutes', 'to get your store online'],
  ['bi-grid-1x2', `${heroTemplates.filter((t) => t.demo).length + intlTemplates.length} designs`, 'ready for different businesses'],
  ['bi-currency-rupee', '₹0 setup', 'website, admin panel and SSL free'],
  ['bi-phone', '100% mobile', 'for you and your customers'],
];

// Countries Ownezzy has ready stores for, with the currency their prices are shown in (international demo stores)
const WORLDWIDE = [
  ['🇮🇳', 'India', 'Rupees (₹)'], ['🇺🇸', 'USA', 'US dollars ($)'], ['🇨🇦', 'Canada', 'Canadian dollars (CA$)'], ['🇬🇧', 'United Kingdom', 'Pounds (£)'],
  ['🇩🇪', 'Germany', 'Euros (€)'], ['🇳🇱', 'Netherlands', 'Euros (€)'], ['🇦🇪', 'UAE', 'Dirhams (AED)'], ['🇸🇦', 'Saudi Arabia', 'Riyals (SAR)'],
  ['🇯🇵', 'Japan', 'Yen (¥)'], ['🇦🇺', 'Australia', 'Australian dollars (A$)'],
];

const OFFER = [
  ['bi-shop-window', 'A ready-made store', 'Pick a design made for your kind of business: plants, pet supplies, aquarium, gifts, groceries, fruits and vegetables, soaps and perfumes or clothing. Your store opens with a ready layout and placeholder categories and products, so you add only your own products.'],
  ['bi-cart-check', 'Shopping your customers know', 'Product pages, offers, combo packs, a cart that remembers what was added, customer accounts with their order history, order updates, product reviews and a WhatsApp button on every page.'],
  ['bi-wallet2', 'Cash on Delivery and UPI', 'Take Cash on Delivery orders from day one. Add your UPI ID to get paid directly, or connect Razorpay for UPI, cards and wallets that are confirmed automatically.'],
  ['bi-truck', 'Delivery made simple', 'Set your delivery charge, free delivery above an amount and GST once. Book couriers like Delhivery, Bluedart and Ekart through Shiprocket and track every parcel from your orders.'],
  ['bi-globe2', 'Your own web address', 'Every store gets a free shopname.ownezzy.com address with SSL security. Use your own domain like shopname.com on a paid plan. Each store also gets its own blog and search engine settings.'],
  ['bi-whatsapp', 'Help on WhatsApp', 'A guided tour on your first sign-in, a setup checklist and a "How to" guide inside the admin panel, and our team on WhatsApp whenever you need help.'],
];

// The admin panel, as a store owner sees it (apps/admin)
const ADMIN = [
  ['bi-speedometer2', 'Overview', 'Your websites, plans and orders at a glance: total, pending, in transit and delivered, plus a checklist of what is left to set up.'],
  ['bi-box-seam', 'Order management', 'Pick a website and see all its orders. A new order rings until you accept or reject it. Update its status, edit details, message the customer on WhatsApp, book a courier and export to Excel or CSV.'],
  ['bi-window-sidebar', 'Website editor', 'Change your logo, theme colour, banners, home page cards, categories and products (photos, prices, stock, featured products, offers and combos) with a live preview before you save.'],
  ['bi-people', 'Staff', 'Add the people who help you and choose what each can do: orders, products and content, or settings.'],
  ['bi-graph-up-arrow', 'Analytics', 'Visits, orders and sales of all your stores, or one at a time.'],
  ['bi-arrow-left-right', 'Data migration', 'Copy your products, categories and photos from Shopify, WooCommerce or another website, with a backup and a one-tap undo.'],
  ['bi-megaphone', 'Marketing', 'Make a banner for your store in your colours and download it to share on Instagram, Facebook and WhatsApp.'],
  ['bi-receipt', 'Billing and limits', 'Pay for your plan by UPI and see your payments. Every website has 2 GB of image storage and 2,000 emails a month, with add-ons if you need more.'],
  ['bi-bell', 'Alerts and app', 'A bell for new orders and limits, notifications on your phone, and an Install app button to add the admin panel to your home screen.'],
];

const VALUES = [
  ['Simple first', 'If a shop owner needs a manual, we have built it wrong. Every screen is made to be used on a phone, by anyone.'],
  ['Fair pricing', 'We never charge for building or maintaining your website. You pay only for storage and servers, and third-party costs like a domain name.'],
  ['Made for India', 'Cash on Delivery, UPI, GST, shipping charges and WhatsApp are built in, because that is how Indian customers buy.'],
  ['Your shop, your brand', 'Your name, your logo, your colours and your customers. Ownezzy stays in the background.'],
];

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export default function AboutPage() {
  const contact = useOwnezzyContact();
  useEffect(() => {
    const url = 'https://ownezzy.com/about';
    document.title = TITLE;
    setMeta('meta[name="description"]', 'content', DESCRIPTION);
    setMeta('link[rel="canonical"]', 'href', url);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:title"]', 'content', TITLE);
    setMeta('meta[property="og:description"]', 'content', DESCRIPTION);
    setMeta('meta[name="twitter:title"]', 'content', TITLE);
    setMeta('meta[name="twitter:description"]', 'content', DESCRIPTION);

    const data = document.createElement('script');
    data.type = 'application/ld+json';
    data.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Ownezzy',
      url,
      description: DESCRIPTION,
      inLanguage: 'en-IN',
      isPartOf: { '@id': 'https://ownezzy.com/#website' },
      about: { '@id': 'https://ownezzy.com/#organization' },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ownezzy.com/' },
          { '@type': 'ListItem', position: 2, name: 'About Us', item: url },
        ],
      },
    });
    document.head.appendChild(data);
    window.scrollTo(0, 0);
    return () => data.remove();
  }, []);

  return (
    <div className="app-shell">
      <AnnouncementBar />
      <Navbar />
      <main className="ab-page">
        <section className="ab-hero">
          <div className="container">
            <nav className="ab-crumbs" aria-label="Breadcrumb"><a href="/">Home</a> <span aria-hidden="true">/</span> About Us</nav>
            <span className="ab-kicker">ABOUT OWNEZZY</span>
            <h1>Helping every shop, in India and worldwide, <span>sell online</span></h1>
            <p>
              Ownezzy gives small shops, home businesses and social media sellers a ready-made website of their own.
              No developer, no coding and no setup fee: pick a design, add your products and start taking orders in minutes.
            </p>
            <div className="ab-hero-actions">
              <a className="ab-btn ab-btn-main" href="/#select-website">See website designs <i className="bi bi-arrow-right" /></a>
              <a className="ab-btn ab-btn-soft" href="/#plans">View plans</a>
            </div>
            <div className="ab-stats">
              {STATS.map(([icon, value, label]) => (
                <div className="ab-stat" key={label}>
                  <i className={`bi ${icon}`} aria-hidden="true" />
                  <b>{value}</b>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ab-section">
          <div className="container ab-story">
            <div>
              <span className="ab-kicker">OUR STORY</span>
              <h2>Why we built Ownezzy</h2>
            </div>
            <div className="ab-story-text">
              <p>
                Across India, sellers run real businesses on Instagram, Facebook and WhatsApp. They post videos, answer the same
                price questions in DMs all day and note orders by hand. Many never get a website because developers are costly,
                online store builders are complicated, and most are not made for how India buys.
              </p>
              <p>
                We started Ownezzy to change that. Every shop owner should have their own online store, with their name, their
                products and their prices, as easily as they open a social media page. Ownezzy builds the website for you, keeps
                it running and stays out of the way, so you can focus on your products and your customers.
              </p>
            </div>
          </div>
        </section>

        {/* Ownezzy worldwide: the countries with ready stores and their currencies */}
        <section className="ab-section ab-worldwide">
          <div className="container">
            <div className="ab-heading">
              <span className="ab-kicker">OWNEZZY WORLDWIDE</span>
              <h2>Websites for home businesses around the world</h2>
              <p>
                Ownezzy started in India and now builds stores for sellers worldwide. Pick a ready store made for your country, with
                prices in your own currency, and start selling in minutes from Dubai, Toronto, London, Berlin, Amsterdam, Riyadh, Tokyo,
                Melbourne, New York or Mumbai.
              </p>
            </div>
            <div className="ab-countries">
              {WORLDWIDE.map(([flag, country, money]) => (
                <div className="ab-country" key={country}>
                  <span aria-hidden="true">{flag}</span>
                  <b>{country}</b>
                  <small>{money}</small>
                </div>
              ))}
            </div>
            <p className="ab-worldwide-note">
              <a href="/websites">See the ready stores for every country →</a>
            </p>
          </div>
        </section>

        <section className="ab-section ab-tinted">
          <div className="container">
            <div className="ab-heading">
              <span className="ab-kicker">WHAT YOU GET</span>
              <h2>Everything a small business needs to sell online</h2>
            </div>
            <div className="ab-grid">
              {OFFER.map(([icon, title, text]) => (
                <article className="ab-card" key={title}>
                  <i className={`bi ${icon}`} aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ab-section">
          <div className="container">
            <div className="ab-heading">
              <span className="ab-kicker">THE ADMIN PANEL</span>
              <h2>Run your whole business from one admin panel</h2>
              <p className="ab-heading-text">Free with every store, made for your phone. Sign in at admin.ownezzy.com and a short guided tour shows you around.</p>
            </div>
            <div className="ab-grid ab-admin-grid">
              {ADMIN.map(([icon, title, text]) => (
                <article className="ab-card" key={title}>
                  <i className={`bi ${icon}`} aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ab-section ab-tinted">
          <div className="container">
            <div className="ab-heading">
              <span className="ab-kicker">WHO IT IS FOR</span>
              <h2>Built for sellers who are ready to grow</h2>
            </div>
            <ul className="ab-who">
              <li><i className="bi bi-instagram" /> Instagram, Facebook and WhatsApp sellers who sell through videos and DMs</li>
              <li><i className="bi bi-shop" /> Local shops that want customers beyond their street</li>
              <li><i className="bi bi-house-heart" /> Home businesses: handmade soaps, gifts, food and crafts</li>
              <li><i className="bi bi-people" /> Agencies and resellers who set up stores for many shops (Franchise plan)</li>
            </ul>
          </div>
        </section>

        <section className="ab-section">
          <div className="container">
            <div className="ab-heading">
              <span className="ab-kicker">WHAT WE BELIEVE</span>
              <h2>Our values</h2>
            </div>
            <div className="ab-values">
              {VALUES.map(([title, text], i) => (
                <article key={title}>
                  <span className="ab-num">{String(i + 1).padStart(2, '0')}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ab-section">
          <div className="container">
            <div className="ab-cta">
              <h2>Get your shop online today</h2>
              <p>Choose a design, see the demo website and get the same website for your shop, free.</p>
              <div className="ab-hero-actions">
                <a className="ab-btn ab-btn-main" href="/#select-website">Get Website <i className="bi bi-arrow-right" /></a>
                <a className="ab-btn ab-btn-ghost" href={whatsappLink(contact, 'Hi, I want a website for my shop.')} target="_blank" rel="noreferrer">
                  <i className="bi bi-whatsapp" /> Talk to us
                </a>
              </div>
            </div>
          </div>
        </section>
        <SeoArticle article={ABOUT_ARTICLE} />
      </main>
      <Footer />
    </div>
  );
}
